import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, AlertTriangle, Clock, ArrowUpRight, X, Loader2, ChevronRight, Database, Shield, Monitor, Building2, ExternalLink, CalendarDays, Ticket, ListChecks, Settings, CheckCircle, Printer, AtSign, MessageSquare, PauseCircle, Package, ClipboardCheck, TrendingUp } from 'lucide-react';
import { apiFetch, okOrThrow } from '../lib/api';
import { useToast } from './TechComponents';
import { openSharedEventStream } from '../lib/eventStream';
import { getNotificationLink } from '../lib/notificationLink';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import BandeauPourMoi, { adresseListe, noterToutVoir } from './BandeauPourMoi';

// Types visibles pour un admin technicien mais non-cliquables (voit qu'il y a
// un souci, mais ne peut pas accéder aux pages d'admin sous-jacentes).
// Pour l'instant : toutes les alertes système. Superadmin = accès complet.
const SYSTEM_NONCLICKABLE_TYPES = new Set(['system_critical', 'system_warning', 'task_failure']);

// ── Category definitions (v2 — force cache bust) ────────────────────────────

const CATEGORIES = {
  acronis: {
    label: 'Sauvegardes Acronis',
    icon: Database,
    color: '#2563a8',
    types: ['acronis_critical_alert', 'acronis_stale_backup', 'acronis_panne_commune'],
  },
  // Meteris Backup (contrat du lot 5 § 7.4) : catégorie « sauvegarde » de NOTIFICATION_TYPES (portail).
  sauvegarde: {
    label: 'Sauvegardes (postes et utilisateurs)',
    icon: Database,
    color: '#0f766e',
    types: ['sauvegarde_retard', 'sauvegarde_place_faible', 'sauvegarde_vss_inefficace', 'sauvegarde_certificat',
      // Contrôle des données (30/10/2026) : sauvegardes utilisateurs et dépôts des postes, mêmes mots.
      'sauvegarde_controle_echec', 'sauvegarde_non_verifiee'],
  },
  eset_devices: {
    label: 'Postes ESET',
    icon: Monitor,
    color: '#e74c3c',
    types: ['eset_critical_device', 'agent_deploy_abandoned', 'agent_deploy_abandoned_restricted'],
  },
  eset_licenses: {
    label: 'Licences ESET',
    icon: Shield,
    color: '#8e44ad',
    types: ['trial_expiry_soon', 'trial_expired', 'trial_convert_full', 'license_expiry_safety_net'],
  },
  calendar: {
    label: 'Interventions & RDV',
    icon: CalendarDays,
    color: '#059669',
    types: ['appointment_reminder'],
  },
  ticketing: {
    label: 'Tickets',
    icon: Ticket,
    color: '#2563eb',
    types: ['ticket_event', 'ticket_sla_warning', 'ticket_sla_breach', 'ticket_pending_stale', 'mention', 'followup', 'task_assigned', 'ticket.management_reply', 'ticket.management_reopened', 'daily_log_reminder', 'daily_log_blocker', 'daily_log_blocker_resolved', 'daily_log_missing', 'poste_disponible'],
  },
  conges: {
    label: 'Congés',
    icon: CalendarDays,
    color: '#0d9488',
    types: ['hr_leave_request', 'hr_leave_potentiel', 'hr_leave_decision', 'hr_leave_cancelled'],
  },
  supply_orders: {
    label: 'Commandes',
    icon: Package,
    color: '#f59e0b',
    types: ['supply_order.overdue'],
  },
  kpax: {
    label: 'Imprimantes',
    icon: Printer,
    color: '#9b59b6',
    types: ['kpax_supply_info', 'kpax_supply_warning', 'kpax_supply_critical'],
  },
  // CRM (24/09/2026) : relance d'une opportunité arrivée à son heure, lead non annoncé sur Teams.
  // La destination est `data.target_url` (/crm/deals/{id}), que getNotificationLink sait lire.
  commercial: {
    label: 'Commercial',
    icon: TrendingUp,
    color: '#7c3aed',
    types: ['crm_follow_up_due', 'crm_lead_announce_failed'],
  },
  system: {
    label: 'Systeme',
    icon: AlertTriangle,
    color: '#dc2626',
    types: ['task_failure', 'system_critical', 'system_warning', 'agent_inventory_stale', 'agent_securite'],
  },
};

const TYPE_CONFIG = {
  trial_expiry_soon: { icon: AlertTriangle, color: 'var(--warning)', label: 'Trial expire bientot' },
  trial_expired: { icon: AlertTriangle, color: 'var(--error)', label: 'Trial expire' },
  trial_convert_full: { icon: ArrowUpRight, color: '#8e44ad', label: 'Conversion Full' },
  acronis_critical_alert: { icon: AlertTriangle, color: 'var(--error)', label: 'Alerte critique' },
  acronis_stale_backup: { icon: Clock, color: 'var(--warning)', label: 'Sauvegarde obsolete' },
  acronis_panne_commune: { icon: AlertTriangle, color: 'var(--error)', label: 'Panne commune des sauvegardes' },
  // Meteris Backup (contrat du lot 5 § 7.4) ; le titre de la notification nomme le poste et le plan.
  sauvegarde_retard: { icon: Clock, color: 'var(--warning)', label: 'Sauvegarde en retard' },
  sauvegarde_place_faible: { icon: Database, color: 'var(--warning)', label: 'Place faible' },
  sauvegarde_vss_inefficace: { icon: AlertTriangle, color: 'var(--warning)', label: 'Instantané inefficace' },
  sauvegarde_certificat: { icon: Shield, color: 'var(--error)', label: 'Certificat du poste' },
  sauvegarde_controle_echec: { icon: AlertTriangle, color: 'var(--error)', label: 'Contrôle des données en échec' },
  sauvegarde_non_verifiee: { icon: Clock, color: 'var(--warning)', label: 'Sauvegarde non vérifiée' },
  eset_critical_device: { icon: Monitor, color: 'var(--error)', label: 'Poste critique' },
  agent_deploy_abandoned: { icon: Monitor, color: '#e67e22', label: 'Agent non deploye' },
  agent_deploy_abandoned_restricted: { icon: Monitor, color: '#e67e22', label: 'Agent non deploye' },
  appointment_reminder: { icon: CalendarDays, color: '#059669', label: 'Rappel' },
  task_failure: { icon: AlertTriangle, color: '#dc2626', label: 'Echec tache planifiee' },
  system_critical: { icon: AlertTriangle, color: '#dc2626', label: 'Metrique critique' },
  system_warning: { icon: AlertTriangle, color: '#e67e22', label: 'Metrique warning' },
  agent_inventory_stale: { icon: Monitor, color: '#e67e22', label: 'Inventaire fige' },
  // Agent 2.0 (contrats § 7.1) : alertes de sécurité des agents, superadmin seul (NOTIFICATION_TYPES).
  agent_securite: { icon: Shield, color: '#dc2626', label: 'Sécurité des agents' },
  ticket_event: { icon: Ticket, color: '#2563eb', label: 'Ticket' },
  mention: { icon: AtSign, color: '#8b5cf6', label: 'Mention' },
  followup: { icon: MessageSquare, color: '#2563eb', label: 'Nouveau suivi' },
  task_assigned: { icon: ListChecks, color: '#059669', label: 'Tache assignee' },
  license_expiry_safety_net: { icon: Shield, color: '#e67e22', label: 'Licence expire' },
  ticket_sla_warning: { icon: Clock, color: '#e67e22', label: 'SLA attention' },
  ticket_sla_breach: { icon: AlertTriangle, color: '#dc2626', label: 'SLA depasse' },
  ticket_pending_stale: { icon: PauseCircle, color: '#9333ea', label: 'Relance en attente' },
  'supply_order.overdue': { icon: Package, color: '#f59e0b', label: 'Rappel commande' },
  'ticket.management_reply':    { icon: ClipboardCheck, color: '#92400e', label: 'Réponse en gestion' },
  'ticket.management_reopened': { icon: ClipboardCheck, color: '#92400e', label: 'Retour de gestion' },
  daily_log_reminder:           { icon: Clock, color: '#e67e22', label: 'Saisie quotidienne' },
  daily_log_blocker:            { icon: AlertTriangle, color: '#c0392b', label: 'Bloquant' },
  daily_log_blocker_resolved:   { icon: CheckCircle, color: '#1a7f4b', label: 'Bloquant traité' },
  daily_log_missing:            { icon: ClipboardCheck, color: '#c0392b', label: 'Journal non saisi' },
  // Congés (16/09/2026) : nominatives, lien posé par hr_leave_notify_svc.
  hr_leave_request:             { icon: CalendarDays, color: '#0d9488', label: 'Demande de congés' },
  hr_leave_potentiel:           { icon: CalendarDays, color: '#64748b', label: 'Jour potentiel' },
  hr_leave_decision:            { icon: CheckCircle, color: '#0d9488', label: 'Décision congés' },
  hr_leave_cancelled:           { icon: CalendarDays, color: '#64748b', label: 'Congés annulés' },
  // Lot C « poste disponible » (20/09/2026) : la notification porte un ticket, elle vit donc dans
  // la catégorie Tickets. 🐤 LE LIBELLÉ NE DIT JAMAIS « DISPONIBLE » SEUL — le mot désigne deux
  // états OPPOSÉS selon qui parle (« quelqu'un s'est assis, appelle le client » côté présence,
  // « personne n'est dessus, tu peux intervenir » côté libre), et c'est l'invariant que tout ce
  // lot protège. Sans cette entrée, le repli `label: n.type` affichait `poste_disponible` tel
  // quel au technicien : exactement le mot interdit, sur l'écran qu'il regarde.
  // Le TITRE de la notification, lui, dit lequel des deux états (`ENTETES`, côté serveur).
  poste_disponible:             { icon: Monitor, color: '#0ea5e9', label: 'Alerte poste' },
  crm_follow_up_due:            { icon: TrendingUp, color: '#7c3aed', label: 'Relance commerciale' },
  crm_lead_announce_failed:     { icon: AlertTriangle, color: '#dc2626', label: 'Lead non annoncé' },
};

const SEVERITY_ORDER = { critical: 0, error: 1, warning: 2, info: 3, pending: 3 };

// ── Helpers ─────────────────────────────────────────────────────────────────

function getSeverity(n) {
  if (n.data?.severity) return n.data.severity;
  if (n.severity) return n.severity;
  if (n.type === 'acronis_critical_alert' || n.type === 'acronis_panne_commune' || n.type === 'trial_expired' || n.type === 'eset_critical_device') return 'critical';
  if (n.type === 'appointment_reminder' && (n.data?.overdue || n.data?.offset_minutes <= 15)) return 'critical';
  if (n.type === 'appointment_reminder') return 'warning';
  if (n.type === 'acronis_stale_backup' || n.type === 'trial_expiry_soon') return 'warning';
  return 'info';
}

// Date de report d'un item (snoozed_until côté system, reminder_paused_until côté commande).
function getSnoozedUntil(n) {
  const v = n.snoozed_until || n.reminder_paused_until;
  return v && v > new Date().toISOString() ? v : null;
}
function fmtDay(iso) {
  try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso?.slice(0, 10) || ''; }
}

function isCritical(sev) {
  return sev === 'critical' || sev === 'error';
}

function getCategoryKey(n) {
  for (const [key, cat] of Object.entries(CATEGORIES)) {
    // Les notifs ticketing portent `event` (ex supply_order.overdue) et pas `type` :
    // on teste les deux pour que les commandes tombent dans la catégorie "Commandes".
    if (cat.types.includes(n.type) || cat.types.includes(n.event)) return key;
  }
  return 'other';
}

function getCompany(n) {
  return n.data?.company || n.data?.tenant_name || n.data?.client_name || '';
}

function severityColor(sev) {
  if (sev === 'critical' || sev === 'error') return 'var(--error)';
  if (sev === 'warning') return 'var(--warning)';
  return 'var(--text-muted)';
}

function severityLabel(sev) {
  if (sev === 'critical') return 'CRITIQUE';
  if (sev === 'error') return 'ERREUR';
  if (sev === 'warning') return 'ATTENTION';
  return 'INFO';
}

function worstSeverity(items) {
  return items.reduce((worst, n) => {
    const s = getSeverity(n);
    return (SEVERITY_ORDER[s] ?? 9) < (SEVERITY_ORDER[worst] ?? 9) ? s : worst;
  }, 'info');
}

/** Get a link for a category group (without specific search) */
function getCategoryLink(catKey) {
  switch (catKey) {
    case 'acronis': return '/parc/sauvegardes?tab=alerts';
    case 'eset_devices': return '/parc/ordinateurs?filter=critical';
    case 'eset_licenses': return '/eset-licenses';
    case 'calendar': return '/assistant/appointments';
    case 'ticketing': return '/tickets';
    default: return null;
  }
}

// ── Component ───────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const [count, setCount] = useState(0);
  const [nominCount, setNominCount] = useState(0);
  const [worstSev, setWorstSev] = useState('info');
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissLoading, setDismissLoading] = useState(null);
  const sseRef = useRef(null);
  // Filtre « Pour moi » du dirigeant (accueil de pilotage, plan 5) : le serveur décide (`pour_moi.actif`) ;
  // « Tout voir » ne vaut que le temps de l'ouverture (remis à « Pour moi » à chaque ouverture).
  const [infoPourMoi, setInfoPourMoi] = useState(null);
  const [toutVoir, setToutVoir] = useState(false);
  const toutVoirRef = useRef(false);
  const requeteListeRef = useRef(0);   // seule la DERNIÈRE réponse de la liste s'affiche (bascule, SSE, ouverture)

  const fetchCount = useCallback(() => {
    apiFetch(adresseListe('/api/notifications/count', false))   // la pastille ne compte que « Pour moi »
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          // « Tout aligné » (bug cloche, William 25/09) : verte = « Pour vous » à traiter
          // (nominatives + tickets, toujours ciblés), rouge = le reste. Reportées exclues côté API.
          const nomin = d.nominative_count || 0;
          setCount(Math.max(0, (d.system_count ?? 0) - nomin));
          setNominCount(nomin + (d.ticketing_count || 0));
          setWorstSev(d.worst_severity || 'info');
        }
      })
      .catch(() => {});
  }, []);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const numero = ++requeteListeRef.current;
      const r = await apiFetch(adresseListe('/api/notifications', toutVoirRef.current));
      const d = await r.json();
      if (numero !== requeteListeRef.current) return;
      if (d.ok) {
        setNotifications(d.notifications);
        setInfoPourMoi(d.pour_moi || null);
      }
    } catch (e) {}
    setLoading(false);
  }, []);

  // Initial fetch + polling fallback
  useEffect(() => {
    fetchCount();
    // Fetch notifications at mount so the dropdown has content when opened
    fetchNotifications();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [fetchCount, fetchNotifications]);

  // SSE listener for real-time notification updates
  useEffect(() => {
    const es = openSharedEventStream()  /* un seul flux SSE par onglet, cf lib/eventStream.js */;
    sseRef.current = es;

    es.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        if (data.type === 'notification') {
          fetchCount();
          if (open) fetchNotifications();
        }
        // @mention notification — show toast if current user is mentioned
        if (data.type === 'mention' && data.mentioned_users) {
          // Le cookie « user_email » n'a jamais existé et `|| length > 0` rendait
          // la garde toujours vraie (« un OU dont un terme est une constante
          // n'est pas une garde »). L'utilisateur courant vient de useAuth.
          const myEmail = (user?.email || '').toLowerCase();
          if (myEmail && data.mentioned_users.map(e => String(e).toLowerCase()).includes(myEmail)) {
            fetchCount();
            if (open) fetchNotifications();
          }
        }
      } catch {}
    };

    es.onerror = () => {
      // SSE will auto-reconnect; fallback to polling is already in place
    };

    return () => {
      es.close();
      sseRef.current = null;
    };
  }, [fetchCount, fetchNotifications, open]);

  const handleOpen = () => {
    const willOpen = !open;
    setOpen(willOpen);
    // Always refresh when opening — guarantees fresh content after navigation
    if (willOpen) {
      toutVoirRef.current = false;
      setToutVoir(false);
      fetchNotifications();
      fetchCount();
    }
  };

  const changerVue = (tout) => {
    toutVoirRef.current = tout;
    setToutVoir(tout);
    if (tout) noterToutVoir();
    fetchNotifications();
  };

  // Dispatch notif IDs to the right endpoint based on source (two independent
  // stores: col_notifications = system; col_tk_notifications = ticketing events
  // like mention/followup/task_assigned).
  const dispatchDismissBySource = async (notifs) => {
    const systemIds = notifs.filter(n => n.source !== 'ticketing').map(n => n.id).filter(Boolean);
    const tkIds = notifs.filter(n => n.source === 'ticketing').map(n => n.id).filter(Boolean);
    const calls = [];
    if (systemIds.length) {
      calls.push(apiFetch('/api/notifications/dismiss-bulk', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: systemIds }),
      }));
    }
    if (tkIds.length) {
      calls.push(apiFetch('/api/notifications/mark-read', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notification_ids: tkIds }),
      }));
    }
    if (calls.length) await Promise.all(calls);
  };

  // Optimistic dismiss: remove from UI immediately, API call in background.
  const doDismiss = async (notifId, allIds) => {
    const idList = allIds && allIds.length > 1 ? allIds : [notifId];
    const idsToRemove = new Set(idList);
    const targetNotifs = notifications.filter(n => idsToRemove.has(n.id));
    setNotifications(prev => prev.map(n => idsToRemove.has(n.id) ? { ...n, _user_dismissed: true } : n));
    setCount(c => Math.max(0, c - idsToRemove.size));
    try {
      await dispatchDismissBySource(targetNotifs);
      fetchCount();
    } catch {}
  };

  const doSnoozeUntil = async (n, untilIso) => {
    setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, snoozed_until: untilIso, reminder_paused_until: untilIso } : x));
    try {
      if (n.source === 'ticketing' && String(n.event || '').startsWith('supply_order') && n.task_id) {
        await apiFetch(`/api/tk/tickets/${n.ticket_id}/tasks/${n.task_id}/snooze`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ until: untilIso }),
        }).then(okOrThrow);
      } else {
        await apiFetch(`/api/notifications/${n.id}/snooze`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ until: untilIso }),
        }).then(okOrThrow);
      }
      fetchCount();
    } catch (err) {
      // Le report affiché plus haut n'est pas enregistré : on le dit et on recharge l'état réel du serveur.
      toast.error('Report impossible : ' + err.message);
      fetchNotifications();
    }
  };

  const doDismissAll = async () => {
    // « Tout ignorer (sauf critiques) » : le libellé le promettait, le geste
    // ignorait aussi les critiques (dismiss_all sans filtre). Une critique ne
    // s'éteint qu'une par une, comme sur la ligne unitaire.
    const toDismiss = notifications.filter(n => n.status === 'pending' && !n._user_dismissed && !isCritical(getSeverity(n)));
    const ids = new Set(toDismiss.map(n => n.id));
    const removedCount = toDismiss.length;
    // Optimistic: clear non-critical local
    setNotifications(prev => prev.map(n => (ids.has(n.id) ? { ...n, _user_dismissed: true } : n)));
    setCount(c => Math.max(0, c - removedCount));
    try {
      // Vue « Pour moi » (plan 5) : n'ignorer QUE ce qui est affiché — dismiss_all et mark-all-read toucheraient aussi
      // ce que le filtre cache (et `is_read` d'une notification de ticket partagée vaut pour tous ses destinataires).
      if (infoPourMoi && infoPourMoi.actif && !toutVoirRef.current) {
        await dispatchDismissBySource(toDismiss);
        fetchCount();
        return;
      }
      // Two independent stores: col_notifications (system) + col_tk_notifications (ticketing)
      await Promise.all([
        apiFetch('/api/notifications/dismiss-bulk', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dismiss_all: true, exclude_critical: true }),
        }).then(okOrThrow),
        apiFetch('/api/notifications/mark-all-read', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: '{}',
        }).then(okOrThrow),
      ]);
      fetchCount();
    } catch (err) {
      // Les notifications masquées plus haut ne le sont pas côté serveur : on le dit et on recharge.
      toast.error('Impossible de tout ignorer : ' + err.message);
      fetchNotifications();
    }
  };

  const doDismissCategory = async (catKey) => {
    // Collect notifs visibly in this category — works for all categories
    // including 'other' (types not mapped in any frontend category, which the
    // backend category=<name> lookup cannot resolve).
    let matching;
    if (catKey === 'other') {
      const knownTypes = new Set(Object.values(CATEGORIES).flatMap(c => c.types));
      matching = notifications.filter(n =>
        !knownTypes.has(n.type) && n.status === 'pending' && !n._user_dismissed
      );
    } else {
      const cat = CATEGORIES[catKey];
      if (!cat) return;
      const types = new Set(cat.types);
      matching = notifications.filter(n =>
        types.has(n.type) && n.status === 'pending' && !n._user_dismissed
      );
    }
    const idsToRemove = new Set(matching.map(n => n.id));
    // Optimistic
    setNotifications(prev => prev.map(n => idsToRemove.has(n.id) ? { ...n, _user_dismissed: true } : n));
    setCount(c => Math.max(0, c - matching.length));
    try {
      await dispatchDismissBySource(matching);
      fetchCount();
    } catch {}
  };

  const goTo = (path) => {
    setOpen(false);
    // Force navigation even when already on same route pattern (e.g. /tickets/A -> /tickets/B)
    if (window.location.pathname === path) {
      // Same exact path — force reload
      window.location.href = path;
    } else if (window.location.pathname.startsWith('/tickets/') && path.startsWith('/tickets/')) {
      // Different ticket — navigate to blank first then to target to force remount
      navigate('/tickets', { replace: true });
      setTimeout(() => navigate(path), 0);
    } else {
      navigate(path);
    }
  };

  // ── Group & sort notifications ──────────────────────────────────────────

  // Per-user: filter out notifications dismissed by this user
  // Reportées (snoozed_until futur) : hors sections actives et hors compteurs (bug cloche 25/09).
  const maintenantIso = new Date().toISOString();
  const estReportee = n => !!n.snoozed_until && n.snoozed_until > maintenantIso;
  const aTraiter = notifications.filter(n => n.status === 'pending' && !n._user_dismissed);
  const reportees = aTraiter.filter(estReportee);
  const pending = aTraiter.filter(n => !estReportee(n));
  const resolved = notifications.filter(n => n.status !== 'pending' || n._user_dismissed);

  const sorted = [...pending].sort((a, b) => {
    const sa = SEVERITY_ORDER[getSeverity(a)] ?? 9;
    const sb = SEVERITY_ORDER[getSeverity(b)] ?? 9;
    if (sa !== sb) return sa - sb;
    return (b.created_at || '').localeCompare(a.created_at || '');
  });

  // ── Deduplicate: group notifications with same type+message (keep most recent, show count) ──
  const deduped = [];
  const seen = new Map(); // key -> index in deduped
  for (const n of sorted) {
    // `data.deal_id` (revue finale CRM relances, point 2) : « Relance prévue » et l'échec d'annonce
    // d'un lead ont le MÊME message d'une opportunité à l'autre — sans lui, deux opportunités
    // devenaient une seule ligne (le clic n'ouvrait que la 1re). Vide pour tous les autres types.
    const groupKey = `${n.type}::${n.message || ''}::${(n.data?.appointment_title || '')}::${n.data?.deal_id || ''}`;
    if (seen.has(groupKey)) {
      const existing = deduped[seen.get(groupKey)];
      existing._count = (existing._count || 1) + 1;
      existing._allIds = [...(existing._allIds || [existing.id]), n.id];
    } else {
      seen.set(groupKey, deduped.length);
      deduped.push({ ...n, _count: 1, _allIds: [n.id] });
    }
  }

  // ── Group by top-level category, then by company within each ──
  // Category priority order: sauvegardes > system > ticketing > imprimantes > eset > calendar
  // Ordre de priorité des sections. Une catégorie déclarée dans CATEGORIES mais absente d'ici
  // est rendue APRÈS celles-ci (avant le 25/09 elle était JETÉE : « Congés », 16→25/09, bug #629).
  const SECTION_ORDER = ['acronis', 'sauvegarde', 'system', 'ticketing', 'commercial', 'supply_orders', 'kpax', 'eset_devices', 'eset_licenses', 'calendar'];

  const sectionMap = {}; // catKey -> { items: [], byCompany: {} }
  const companyOnlyItems = []; // items that don't fit a top-level category

  // « Pour vous » (registre #20266) : ce qui me NOMME passe en tête, déplié, une seule fois.
  const monEmail = user?.email || '';
  // Une notification de tickets est ciblée à la source (parfois via l'ancien `target_user`) : toujours pour moi.
  const estPourMoi = n => n.source === 'ticketing' || (!!monEmail && (n.target_user_ids || []).includes(monEmail));
  const pourVous = deduped.filter(estPourMoi);
  const pourVousByCompany = {};
  for (const n of pourVous) {
    const company = getCompany(n);
    if (company) (pourVousByCompany[company] = pourVousByCompany[company] || []).push(n);
  }

  for (const n of deduped.filter(n => !estPourMoi(n))) {
    const catKey = getCategoryKey(n);
    if (catKey === 'other') {
      companyOnlyItems.push(n);
      continue;
    }
    if (!sectionMap[catKey]) sectionMap[catKey] = { items: [], byCompany: {} };
    sectionMap[catKey].items.push(n);
    const company = getCompany(n);
    if (company) {
      if (!sectionMap[catKey].byCompany[company]) sectionMap[catKey].byCompany[company] = [];
      sectionMap[catKey].byCompany[company].push(n);
    }
  }

  // Build ordered sections
  // Toute catégorie remplie mais hors SECTION_ORDER est rendue à la suite (jamais jetée).
  const ordreSections = [...SECTION_ORDER, ...Object.keys(sectionMap).filter(k => !SECTION_ORDER.includes(k))];
  const sections = ordreSections
    .filter(k => sectionMap[k] && sectionMap[k].items.length > 0)
    .map(catKey => ({
      catKey,
      cat: CATEGORIES[catKey],
      items: sectionMap[catKey].items,
      byCompany: sectionMap[catKey].byCompany,
      totalCount: sectionMap[catKey].items.reduce((s, n) => s + (n._count || 1), 0),
      worstSev: worstSeverity(sectionMap[catKey].items),
    }));

  // Badge
  const badgeCount = count;
  const hasCritical = worstSev === 'critical' || worstSev === 'error' || sorted.some(n => isCritical(getSeverity(n)));

  return (
    <div style={{ position: 'relative' }}>
      <button data-testid="notification-bell" onClick={handleOpen}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6,
          color: 'rgba(255,255,255,0.7)', position: 'relative',
          transition: 'color var(--duration-fast) var(--ease-out)',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#fff'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
      >
        <Bell size={20} />
        {badgeCount > 0 && (
          <span data-testid="notification-count" style={{
            position: 'absolute', top: 0, right: -2,
            background: hasCritical ? 'var(--error)' : 'var(--warning)', color: '#fff',
            fontSize: 9, fontWeight: 800,
            padding: '1px 5px', borderRadius: 10, minWidth: 16, textAlign: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
            animation: hasCritical ? 'notify-dot 2s ease-in-out infinite' : 'none',
            boxShadow: hasCritical ? '0 0 8px rgba(192, 57, 43, 0.4)' : 'none',
          }}>
            {badgeCount}
          </span>
        )}
        {nominCount > 0 && (
          <span data-testid="notification-count-nominative" title="Notifications qui vous nomment" style={{
            position: 'absolute', top: 0, left: -4,
            background: '#0d9488', color: '#fff',
            fontSize: 9, fontWeight: 800,
            padding: '1px 5px', borderRadius: 10, minWidth: 16, textAlign: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
          }}>
            {nominCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="overlay-animate" style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.1)' }} onClick={() => setOpen(false)} />
          <div data-testid="notification-panel" className="modal-animate" style={{
            position: 'fixed', top: 10, left: 248,
            width: 460, maxHeight: '90vh', overflowY: 'auto',
            background: 'var(--surface)',
            borderRadius: 12,
            border: '1px solid var(--border-light)',
            boxShadow: 'var(--shadow-xl)',
            zIndex: 100,
          }}>
            {/* Header */}
            <div style={{
              background: 'var(--primary)', color: '#fff', padding: '12px 16px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderRadius: '12px 12px 0 0',
            }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: 12, letterSpacing: 1 }}>
                NOTIFICATIONS {pending.length > 0 && `(${pending.length})`}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {pending.length > 0 && (
                  <button onClick={doDismissAll} title="Tout ignorer (sauf critiques)"
                    style={{
                      background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                      borderRadius: 6, color: '#fff', cursor: 'pointer', padding: '3px 8px',
                      fontSize: 9, fontWeight: 600, transition: 'background var(--duration-fast)',
                      fontFamily: "'IBM Plex Mono', monospace",
                    }}>
                    TOUT IGNORER
                  </button>
                )}
                <button onClick={() => goTo('/notifications')} title="Centre de notifications"
                  style={{
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: 6, color: '#fff', cursor: 'pointer', padding: 4,
                    display: 'flex', transition: 'background var(--duration-fast)',
                  }}>
                  <ListChecks size={14} />
                </button>
                <button onClick={() => setOpen(false)} style={{
                  background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: 6, color: '#fff', cursor: 'pointer', padding: 4,
                  display: 'flex', transition: 'background var(--duration-fast)',
                }}>
                  <X size={14} />
                </button>
              </div>
            </div>

            <BandeauPourMoi info={infoPourMoi} toutVoir={toutVoir}
              onToutVoir={() => changerVue(true)} onRevenir={() => changerVue(false)} />

            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                <Loader2 size={22} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
                <p style={{ fontSize: 13 }}>Chargement...</p>
              </div>
            ) : pending.length === 0 && reportees.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
                <Bell size={28} style={{ opacity: 0.2, marginBottom: 10 }} />
                <p style={{ fontSize: 13, fontWeight: 500 }}>Aucune notification</p>
              </div>
            ) : (
              <div>
                {/* ── Pour vous : ce qui me nomme, déplié, sans « tout ignorer » en masse ── */}
                {pourVous.length > 0 && (
                  <CollapsibleSection
                    catKey="pour_vous"
                    cat={{ label: 'Pour vous', icon: AtSign, color: '#0d9488' }}
                    items={pourVous}
                    byCompany={pourVousByCompany}
                    totalCount={pourVous.reduce((s, n) => s + (n._count || 1), 0)}
                    worstSev={worstSeverity(pourVous)}
                    goTo={goTo}
                    doDismiss={doDismiss}
                    doSnoozeUntil={doSnoozeUntil}
                    doDismissCategory={null}
                    dismissLoading={dismissLoading}
                    defaultExpanded
                  />
                )}

                {/* ── Sections by category (collapsible) ── */}
                {sections.map(sec => (
                  <CollapsibleSection
                    key={sec.catKey}
                    catKey={sec.catKey}
                    cat={sec.cat}
                    items={sec.items}
                    byCompany={sec.byCompany}
                    totalCount={sec.totalCount}
                    worstSev={sec.worstSev}
                    goTo={goTo}
                    doDismiss={doDismiss}
                    doSnoozeUntil={doSnoozeUntil}
                    doDismissCategory={doDismissCategory}
                    dismissLoading={dismissLoading}
                  />
                ))}

                {/* ── Ungrouped (no category match) ── */}
                {companyOnlyItems.length > 0 && (
                  <CollapsibleSection
                    catKey="other"
                    cat={{ label: 'Autres', icon: Bell, color: 'var(--text-muted)' }}
                    items={companyOnlyItems}
                    byCompany={{}}
                    totalCount={companyOnlyItems.reduce((s, n) => s + (n._count || 1), 0)}
                    worstSev={worstSeverity(companyOnlyItems)}
                    goTo={goTo}
                    doDismiss={doDismiss}
                    doSnoozeUntil={doSnoozeUntil}
                    doDismissCategory={doDismissCategory}
                    dismissLoading={dismissLoading}
                  />
                )}

                {/* ── Reportées (repliées, hors compteurs) ── */}
                {reportees.length > 0 && <ResolvedSection items={reportees} goTo={goTo} reportees />}

                {/* ── Resolved recently (collapsible) ── */}
                {resolved.length > 0 && <ResolvedSection items={resolved} goTo={goTo} />}
              </div>
            )}

            {/* Footer link to notification center */}
            <div
              onClick={() => goTo('/notifications')}
              style={{
                padding: '10px 16px', borderTop: '1px solid var(--border-light)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'var(--primary)',
                transition: 'background var(--duration-fast)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <ListChecks size={12} />
              <span>Voir toutes les notifications</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Collapsible category section ─────────────────────────────────────────────

function CollapsibleSection({ catKey, cat, items, byCompany, totalCount, worstSev, goTo, doDismiss, doSnoozeUntil, doDismissCategory, dismissLoading, defaultExpanded = false }) {
  // Sections repliées par défaut, sauf « Pour vous » (defaultExpanded).
  const [expanded, setExpanded] = useState(defaultExpanded);
  const CatIcon = cat?.icon || Bell;
  const color = cat?.color || 'var(--text-muted)';
  const companies = Object.entries(byCompany);
  const noCompanyItems = items.filter(n => !getCompany(n));

  return (
    <div>
      {/* Section header — clickable to collapse/expand */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          ...sectionHeaderStyle,
          cursor: 'pointer',
          justifyContent: 'space-between',
          borderLeft: `3px solid ${severityColor(worstSev)}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <CatIcon size={11} style={{ color }} />
          <span>{cat?.label || catKey} ({totalCount})</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {doDismissCategory && totalCount > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); doDismissCategory(catKey); }}
              title={`Ignorer toutes les ${cat?.label || catKey}`}
              style={{
                background: 'transparent', border: '1px solid var(--border-light)',
                borderRadius: 4, cursor: 'pointer', padding: '1px 5px',
                color: 'var(--text-muted)', fontSize: 9, fontWeight: 700,
                fontFamily: "'IBM Plex Mono', monospace",
              }}
            >TOUT IGNORER</button>
          )}
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontWeight: 800, fontSize: 13,
            color: severityColor(worstSev),
          }}>
            {totalCount}
          </span>
          <ChevronRight size={12} style={{
            color: 'var(--text-muted)',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--duration-fast)',
          }} />
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div>
          {/* By company within this category (collapsible) */}
          {companies.map(([company, companyItems]) => (
            <CompanySubGroup key={company} company={company} items={companyItems} goTo={goTo} doDismiss={doDismiss} doSnoozeUntil={doSnoozeUntil} dismissLoading={dismissLoading} />
          ))}
          {/* Items without company */}
          {noCompanyItems.map((n, idx) => (
            <NotificationRow key={n.id} n={n} idx={idx} goTo={goTo} doDismiss={doDismiss} doSnoozeUntil={doSnoozeUntil} dismissLoading={dismissLoading} />
          ))}
        </div>
      )}
    </div>
  );
}


// ── Company sub-group (within a category section, collapsible) ──────────────

function CompanySubGroup({ company, items, goTo, doDismiss, doSnoozeUntil, dismissLoading }) {
  const [expanded, setExpanded] = useState(false);
  const count = items.reduce((s, n) => s + (n._count || 1), 0);
  const ws = worstSeverity(items);
  // Try to find client_id from any item in the group for navigation
  const clientId = items.find(n => n.data?.client_id)?.data?.client_id || '';

  return (
    <div>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '5px 16px 5px 20px', fontSize: 11, fontWeight: 700,
          color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          borderBottom: '1px solid var(--border-light)', background: 'var(--bg)',
          cursor: 'pointer', transition: 'background var(--duration-fast)',
          borderLeft: `2px solid ${severityColor(ws)}`,
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface)'}
        onMouseLeave={e => e.currentTarget.style.background = 'var(--bg)'}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <Building2 size={10} style={{ color: 'var(--text-muted)' }} />
          <span
            onClick={e => { if (clientId) { e.stopPropagation(); goTo(`/gestion/societes/${clientId}`); } }}
            style={{ cursor: clientId ? 'pointer' : 'default', textDecoration: clientId ? 'underline' : 'none' }}
            title={clientId ? `Ouvrir la fiche ${company}` : ''}
          >{company}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontWeight: 800, fontSize: 11,
            color: severityColor(ws),
          }}>
            {count}
          </span>
          <ChevronRight size={10} style={{
            color: 'var(--text-muted)',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--duration-fast)',
          }} />
        </div>
      </div>
      {expanded && items.map((n, idx) => (
        <NotificationRow key={n.id} n={n} idx={idx} goTo={goTo} nested doDismiss={doDismiss} doSnoozeUntil={doSnoozeUntil} dismissLoading={dismissLoading} />
      ))}
    </div>
  );
}


// ── Company group row ────────────────────────────────���──────────────────────

function CompanyGroup({ company, types, totalCount, worstSeverity: ws, goTo, doDismiss, doSnoozeUntil, dismissLoading }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--border-light)',
          borderLeft: `3px solid ${severityColor(ws)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer',
          transition: 'background var(--duration-fast)',
          background: expanded ? 'var(--bg)' : 'transparent',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
      >
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
            <span style={{
              fontSize: 9, fontWeight: 800, color: '#fff', padding: '1px 5px', borderRadius: 3,
              background: severityColor(ws), fontFamily: "'IBM Plex Mono', monospace",
            }}>
              {severityLabel(ws)}
            </span>
            <Building2 size={12} style={{ color: 'var(--text-muted)' }} />
            <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--primary)' }}>
              {company}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {types.map(({ catKey, cat, count: cnt }) => {
              const CatIcon = cat?.icon || Bell;
              return (
                <span key={catKey} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <CatIcon size={10} style={{ color: cat?.color || 'var(--text-muted)' }} />
                  {cat?.label || catKey}: {cnt}
                </span>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{
            fontFamily: "'IBM Plex Mono', monospace", fontWeight: 800, fontSize: 16,
            color: severityColor(ws),
          }}>
            {totalCount}
          </span>
          <ChevronRight size={16} style={{
            color: 'var(--text-muted)',
            transform: expanded ? 'rotate(90deg)' : 'none',
            transition: 'transform var(--duration-fast)',
          }} />
        </div>
      </div>

      {/* Expanded detail: list individual notifications with links */}
      {expanded && (
        <div style={{ background: 'var(--bg)', borderBottom: '1px solid var(--border-light)' }}>
          {types.map(({ catKey, cat, items }) => (
            <div key={catKey}>
              {/* Category sub-header with link */}
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  const link = getCategoryLink(catKey);
                  if (link) goTo(link);
                }}
                style={{
                  padding: '6px 16px 6px 28px',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: getCategoryLink(catKey) ? 'pointer' : 'default',
                  borderBottom: '1px solid var(--border-light)',
                  transition: 'background var(--duration-fast)',
                }}
                onMouseEnter={e => { if (getCategoryLink(catKey)) e.currentTarget.style.background = 'var(--surface)'; }}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {cat?.icon && <cat.icon size={12} style={{ color: cat.color }} />}
                  <span style={{ fontWeight: 700, fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {cat?.label || catKey} ({items.length})
                  </span>
                </div>
                {getCategoryLink(catKey) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, color: 'var(--text-muted)' }}>
                    <span>Voir tout</span>
                    <ExternalLink size={10} />
                  </div>
                )}
              </div>
              {/* Individual notifications */}
              {items.map((n, idx) => (
                <NotificationRow key={n.id} n={n} idx={idx} goTo={goTo} nested doDismiss={doDismiss} doSnoozeUntil={doSnoozeUntil} dismissLoading={dismissLoading} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Notification row (clickable link, optional dismiss for non-critical) ──

function NotificationRow({ n, idx, goTo, nested, doDismiss, doSnoozeUntil, dismissLoading }) {
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';
  const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG[n.event] || { icon: Bell, color: 'var(--text-muted)', label: n.type || n.event };
  const sev = getSeverity(n);
  const Icon = cfg.icon;
  // Non-superadmin users see system alerts but can't drill into admin pages:
  // hide the link (no chevron, no navigation, default cursor).
  const systemRestricted = !isSuperadmin && SYSTEM_NONCLICKABLE_TYPES.has(n.type);
  const link = systemRestricted ? null : getNotificationLink(n);
  const isKpax = (n.type || '').startsWith('kpax_');
  const canDismiss = isKpax || !isCritical(sev);
  const isLoading = dismissLoading === n.id;
  const dupCount = n._count || 1;
  const [isDismissing, setIsDismissing] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(false);

  const handleClick = (e) => {
    if (!link) {
      // href="#" would otherwise jump scroll to top — always block that.
      e.preventDefault();
      return;
    }
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    goTo(link);
  };

  // Two safeguards stop the parent <a> from navigating on X click:
  // - preventDefault() blocks the browser's native anchor follow-through
  // - stopPropagation() blocks the JS click bubbling to the row handler
  // Then a 180ms fade+slide makes the dismissal visually explicit.
  const handleDismissClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDismissing(true);
    setTimeout(() => doDismiss(n.id, n._allIds), 180);
  };

  return (
    <a
      href={link || '#'}
      data-testid={`notification-${n.id}`}
      onClick={handleClick}
      style={{
        padding: nested ? '8px 16px 8px 40px' : '10px 16px',
        borderBottom: '1px solid var(--border-light)',
        borderLeft: nested ? 'none' : `3px solid ${severityColor(sev)}`,
        cursor: link ? 'pointer' : 'default',
        display: 'flex', alignItems: 'center', gap: 10,
        transition: 'background var(--duration-fast), opacity 180ms ease-out, transform 180ms ease-out, max-height 180ms ease-out',
        animation: `stagger-in var(--duration-normal) var(--ease-out) ${idx * 30}ms both`,
        textDecoration: 'none', color: 'inherit',
        opacity: isDismissing ? 0 : 1,
        transform: isDismissing ? 'translateX(24px)' : 'translateX(0)',
        pointerEvents: isDismissing ? 'none' : 'auto',
      }}
      onMouseEnter={e => { if (!isDismissing) e.currentTarget.style.background = 'var(--surface)'; }}
      onMouseLeave={e => { if (!isDismissing) e.currentTarget.style.background = 'transparent'; }}
    >
      {/* Severity icon with duplicate badge */}
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: 24, height: 24, borderRadius: 5,
          background: `${severityColor(sev)}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={12} style={{ color: severityColor(sev) }} />
        </div>
        {dupCount > 1 && (
          <span style={{
            position: 'absolute', top: -5, right: -8,
            background: severityColor(sev), color: '#fff',
            fontSize: 8, fontWeight: 800, borderRadius: 6,
            padding: '0 4px', minWidth: 14, textAlign: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
            lineHeight: '14px',
          }}>
            x{dupCount}
          </span>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 1 }}>
          {!nested && (
            <span style={{
              fontSize: 8, fontWeight: 800, color: '#fff', padding: '1px 4px', borderRadius: 3,
              background: severityColor(sev), fontFamily: "'IBM Plex Mono', monospace",
            }}>
              {severityLabel(sev)}
            </span>
          )}
          <span style={{ fontWeight: 600, fontSize: 11, color: 'var(--primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {n.title}
          </span>
        </div>
        <p style={{ fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.3, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {n.message}
        </p>
        {getSnoozedUntil(n) && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 3,
            fontSize: 9, fontWeight: 700, color: '#92400e',
            background: '#fef3c7', borderRadius: 4, padding: '1px 5px',
          }}>
            <Clock size={9} /> Reporté au {fmtDay(getSnoozedUntil(n))}
          </span>
        )}
        <div style={{ fontSize: 8, color: 'var(--text-muted)', fontFamily: "'IBM Plex Mono', monospace", marginTop: 2 }}>
          {new Date(n.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          {dupCount > 1 && ` — ${dupCount} occurrences`}
        </div>
      </div>

      {/* Right side: dismiss (non-critical only) + link indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        {doSnoozeUntil && !isLoading && (
          <>
            <button
              type="button"
              title="Reporter ce rappel à une date"
              onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setSnoozeOpen(v => !v); }}
              style={{
                background: 'transparent', border: '1px solid var(--border-light)',
                borderRadius: 4, cursor: 'pointer', padding: 3, color: '#92400e', display: 'flex',
              }}
            >
              <Clock size={10} />
            </button>
            {snoozeOpen && (
              <input
                type="datetime-local"
                min={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => { if (e.target.value) { setSnoozeOpen(false); doSnoozeUntil(n, new Date(e.target.value).toISOString()); } }}
                style={{ fontSize: 10, padding: '1px 3px', border: '1px solid #d1d5db', borderRadius: 4 }}
              />
            )}
          </>
        )}
        {canDismiss && !isLoading && (
          <button
            type="button"
            data-testid={`notif-dismiss-${n.id}`}
            title={dupCount > 1 ? `Ignorer les ${dupCount} occurrences` : 'Ignorer'}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={handleDismissClick}
            style={{
              background: 'transparent', border: '1px solid var(--border-light)',
              borderRadius: 4, cursor: 'pointer', padding: 3,
              color: 'var(--text-muted)', display: 'flex',
              transition: 'all var(--duration-fast)',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--text-muted)'; e.currentTarget.style.background = 'var(--bg)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-light)'; e.currentTarget.style.background = 'transparent'; }}
          >
            <X size={10} />
          </button>
        )}
        {isLoading && (
          <Loader2 size={12} style={{ color: 'var(--text-muted)', animation: 'spin 1s linear infinite' }} />
        )}
        {link && (
          <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
        )}
      </div>
    </a>
  );
}

// ── Resolved recently section ────────────────────────────────────────────────

function ResolvedSection({ items, goTo, reportees = false }) {
  const [expanded, setExpanded] = useState(false);
  // Limit to 10 most recent
  const recent = items.slice(0, 10);

  return (
    <div style={{ borderTop: '1px solid var(--border-light)' }}>
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '8px 16px', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          color: 'var(--text-muted)', textTransform: 'uppercase',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', background: 'var(--bg)',
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <CheckCircle size={10} style={{ color: 'var(--success)' }} />
          <span>{reportees ? 'Reportées' : 'Resolues recemment'} ({items.length})</span>
        </div>
        <ChevronRight size={10} style={{
          transform: expanded ? 'rotate(90deg)' : 'none',
          transition: 'transform var(--duration-fast)',
        }} />
      </div>
      {expanded && recent.map((n, idx) => {
        const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG[n.event] || { icon: Bell, color: 'var(--text-muted)', label: n.type || n.event };
        const Icon = cfg.icon;
        const link = getNotificationLink(n);
        const handleClick = (e) => {
          if (!link) return;
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
          e.preventDefault();
          goTo(link);
        };
        return (
          <a key={n.id}
            href={link || '#'}
            onClick={handleClick}
            style={{
              padding: '6px 16px', borderBottom: '1px solid var(--border-light)',
              display: 'flex', alignItems: 'center', gap: 8, opacity: 0.6,
              cursor: link ? 'pointer' : 'default',
              transition: 'opacity var(--duration-fast)',
              textDecoration: 'none', color: 'inherit',
            }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={e => e.currentTarget.style.opacity = '0.6'}
          >
            <Icon size={11} style={{ color: 'var(--success)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {n.title}
              </div>
              <div style={{ fontSize: 9, color: 'var(--text-muted)', fontFamily: "'IBM Plex Mono', monospace" }}>
                {n.message?.substring(0, 50)}
              </div>
            </div>
            <span style={{
              fontSize: 8, fontWeight: 600, color: 'var(--success)',
              padding: '1px 5px', borderRadius: 3, background: '#f0fdf4',
              fontFamily: "'IBM Plex Mono', monospace", flexShrink: 0,
            }}>
              {reportees ? `Reporté au ${fmtDay(n.snoozed_until)}`
                : n.status === 'resolved' ? 'RESOLU' : n.status === 'dismissed' ? 'IGNORE' : 'EXPIRE'}
            </span>
          </a>
        );
      })}
    </div>
  );
}


const sectionHeaderStyle = {
  padding: '8px 16px', fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
  color: 'var(--text-muted)', textTransform: 'uppercase',
  display: 'flex', alignItems: 'center', gap: 6,
  borderBottom: '1px solid var(--border-light)', background: 'var(--bg)',
  fontFamily: "'IBM Plex Mono', monospace",
};
